import { Observable } from '@apollo/client';
// import { RetryLink } from '@apollo/client/link/retry';
import QueueLink from 'apollo-link-queue';
import { registerNetworkStatusCallback } from './registerNetworkStatusCallback';
// import SerializingLink from 'apollo-link-serialize';
import { addTrackedQuery, moveTrackedQueryToFailed, removeTrackedQuery, } from './trackedQueries';
import { isPermanentRejection } from './replayErrors';
import { clearInFlight, markInFlight } from './inFlightTrackedQueries';
// const retryLink = new RetryLink({
//   attempts: {
//     max: Infinity,
//     retryIf: (error, _operation) => !!_operation.getContext().tracked,
//   },
// });
const queueLink = new QueueLink();
export const ONLINE_THRESHOLD = '3g';
registerNetworkStatusCallback(ONLINE_THRESHOLD, (isConnected) => (isConnected ? queueLink.open() : queueLink.close()), 2000);
// const serializingLink = (new SerializingLink() as unknown) as ApolloLink;
/**
 * Builds the tracker link with an owner resolver bound in, so the queued
 * entry can be stamped with the identity that queued it (BIL-4928) without
 * this package importing any concrete auth library — see `createOfflineLink`.
 */
const makeAsyncTrackerLink = (resolveOwner) => function asyncTrackerLink(operation, forward) {
    if (forward === undefined)
        return null;
    const context = operation.getContext();
    if (context.tracked === undefined)
        return forward(operation);
    const { operationName, query, variables } = operation;
    // Defer forward() until the owner is resolved and the IDB write has
    // settled so that: (a) the caller knows the mutation is durably queued
    // (with its owner) before the network hop starts, and (b) unsubscription
    // before the write settles cancels the forward subscription.
    return new Observable((observer) => {
        let innerSub = null;
        let cancelled = false;
        let marked = false;
        // The storage write a result triggers (remove, or move to failed).
        // Until it settles the entry is still in the queue, so it must stay
        // marked or a replay could pick it up and send it again.
        let storeUpdate = Promise.resolve();
        // Releases the in-flight mark exactly once, whichever of completion,
        // error or unsubscription comes first.
        const release = () => {
            if (!marked)
                return;
            marked = false;
            clearInFlight(context.id);
        };
        const releaseAfterStoreUpdate = () => {
            storeUpdate.finally(release);
        };
        resolveOwner().then((owner) => {
            if (cancelled)
                return;
            //TODO offline mutations skip queue
            //TODO offline mutations track optimistic id
            const newTrackedQuery = {
                query,
                context,
                variables,
                operationName,
                owner: owner !== null && owner !== void 0 ? owner : undefined,
            };
            addTrackedQuery(newTrackedQuery).then(() => {
                if (cancelled)
                    return;
                markInFlight(context.id);
                marked = true;
                innerSub = forward(operation)
                    .map((result) => {
                    var _a;
                    // A result WITH GraphQL errors is not necessarily a
                    // failure to remove from the queue — the request did
                    // reach the server and got an answer, so `error:` below
                    // never fires for this case. Only a permanent rejection
                    // (every error flagged isBilderitExpectedError, e.g. an
                    // authz denial) is a business decision we must not keep
                    // retrying; it moves to the dead-letter store instead of
                    // being silently deleted. Anything else (unflagged/mixed
                    // errors — an internal server error, for instance) is
                    // left queued so it gets retried, per the transient
                    // default.
                    if ((_a = result.errors) === null || _a === void 0 ? void 0 : _a.length) {
                        if (isPermanentRejection(result.errors)) {
                            storeUpdate = moveTrackedQueryToFailed(newTrackedQuery, result.errors.map((e) => e.message).join(' · ')).catch((err) => console.error('moveTrackedQueryToFailed failed', err));
                        }
                    }
                    else {
                        storeUpdate = removeTrackedQuery(context.id).catch((err) => console.error('removeTrackedQuery failed', err));
                    }
                    return result;
                })
                    .subscribe({
                    next: (v) => observer.next(v),
                    error: (e) => {
                        releaseAfterStoreUpdate();
                        observer.error(e);
                    },
                    complete: () => {
                        releaseAfterStoreUpdate();
                        observer.complete();
                    },
                });
            }, (err) => {
                if (!cancelled)
                    observer.error(err);
            });
        }, (err) => {
            if (!cancelled)
                observer.error(err);
        });
        return () => {
            cancelled = true;
            innerSub === null || innerSub === void 0 ? void 0 : innerSub.unsubscribe();
            releaseAfterStoreUpdate();
        };
    });
};
const queueRequestHandler = (operation, forward) => { var _a; return (_a = 
// `apollo-link-queue` still bundles Apollo types through its own dependency
// tree, so we bridge that mismatch once here instead of leaking it outward.
queueLink.request(operation, forward)) !== null && _a !== void 0 ? _a : null; };
// Accept the consumer's ApolloLink constructor so the generated .d.ts stays
// free of concrete Apollo class imports and works under both node-modules and PnP.
// `options` is required so a caller cannot silently fall back to unowned
// queue entries (BIL-4928).
export const createOfflineLink = (ApolloLinkCtor, options) => ApolloLinkCtor.from([
    makeAsyncTrackerLink(options.resolveOwner),
    queueRequestHandler,
    // serializingLink,
    // retryLink
]);
